import { saveExcelWorkbook } from './excelWorkbook';

const CATEGORY_TYPE_LABELS = {
  music: 'Music',
  station_id: 'Station / ID',
  music_bed: 'Music Bed',
  other: 'Other',
};

const ITEM_HEADERS = ['Title', 'Artist', 'Album', 'Duration', 'Plays', 'Airtime', 'Last played in range', 'Added', 'Asset ID'];

const GROUP_LABELS = {
  asset: 'Asset',
  category: 'Category',
  hour: 'Hour of Day',
  day: 'Date',
};

function formatDurationHms(seconds) {
  const parsed = Number(seconds);
  if (!Number.isFinite(parsed) || parsed <= 0) return '0:00';
  const total = Math.round(parsed);
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const secs = total % 60;
  if (hours > 0) return `${hours}:${String(minutes).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
  return `${minutes}:${String(secs).padStart(2, '0')}`;
}

function formatDateOnly(value) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function formatDateTime(value) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleString();
}

const WIN_ANSI_EXTRA = new Set([
  0x20AC, 0x201A, 0x0192, 0x201E, 0x2026, 0x2020, 0x2021,
  0x02C6, 0x2030, 0x0160, 0x2039, 0x0152, 0x017D,
  0x2018, 0x2019, 0x201C, 0x201D, 0x2022, 0x2013, 0x2014,
  0x02DC, 0x2122, 0x0161, 0x203A, 0x0153, 0x017E, 0x0178,
]);

// jsPDF's standard fonts corrupt a whole string when they meet a character
// outside Windows-1252, so those characters are replaced before drawing.
export function pdfSafe(value) {
  let out = '';
  for (const ch of String(value ?? '')) {
    const code = ch.codePointAt(0);
    if ((code >= 0x20 && code <= 0x7E) || (code >= 0xA0 && code <= 0xFF) || WIN_ANSI_EXTRA.has(code)) {
      out += ch;
    } else if (code === 0x09 || code === 0x0A || code === 0x0D) {
      out += ' ';
    } else {
      out += '?';
    }
  }
  return out;
}

export function pdfSafeRows(rows) {
  return (rows || []).map((row) => row.map((cell) => (
    typeof cell === 'number' ? cell : pdfSafe(cell)
  )));
}

function typeLabel(type) {
  if (type === 'uncategorised') return 'Uncategorised';
  return CATEGORY_TYPE_LABELS[type] || type || 'Other';
}

export function catalogueStationName(catalogue, stations, pickedStationId) {
  const stationId = pickedStationId || catalogue?.station_id;
  if (!stationId) return '';
  const station = (stations || []).find((item) => String(item.id) === String(stationId));
  return station?.name || `Station ${stationId}`;
}

export function catalogueSubtitle(catalogue, { stationName, categoryTypeLabel, categoryName } = {}) {
  return [
    stationName ? `Station: ${stationName}` : 'All stations',
    categoryTypeLabel ? `Type: ${categoryTypeLabel}` : 'All category types',
    categoryName ? `Category: ${categoryName}` : 'All categories',
    catalogueFilterSummary(catalogue) ? `Search: ${catalogueFilterSummary(catalogue)}` : '',
    `Plays from ${catalogue?.from || 'the start'} to ${catalogue?.to || 'now'}`,
  ].filter(Boolean).join('   ');
}

// Describes the search narrowing a catalogue download, so a short list in the
// file is never mistaken for the whole library.
export function catalogueFilterSummary(catalogue) {
  return [
    catalogue?.search ? `“${catalogue.search}”` : '',
    catalogue?.title ? `title “${catalogue.title}”` : '',
    catalogue?.artist ? `artist “${catalogue.artist}”` : '',
  ].filter(Boolean).join(' · ');
}

function itemColumns(item) {
  return [
    item.title || 'Untitled',
    item.artist || '',
    item.album || '',
    formatDurationHms(item.duration),
    Number(item.play_count) || 0,
    formatDurationHms(item.airtime_seconds),
    formatDateTime(item.last_play),
    formatDateOnly(item.created_at),
    item.id ?? '',
  ];
}

function sectionsFromCatalogue(catalogue) {
  const sections = (catalogue?.categories || []).map((category) => {
    const items = category.items || [];
    return {
      name: category.name || 'Untitled category',
      typeLabel: typeLabel(category.category_type),
      itemCount: items.length,
      duration: items.reduce((sum, item) => sum + (Number(item.duration) || 0), 0),
      plays: items.reduce((sum, item) => sum + (Number(item.play_count) || 0), 0),
      airtime: items.reduce((sum, item) => sum + (Number(item.airtime_seconds) || 0), 0),
      items,
    };
  });
  const uncategorised = catalogue?.uncategorised || [];
  if (uncategorised.length) {
    sections.push({
      name: 'Uncategorised',
      typeLabel: 'Uncategorised',
      itemCount: uncategorised.length,
      duration: uncategorised.reduce((sum, item) => sum + (Number(item.duration) || 0), 0),
      plays: uncategorised.reduce((sum, item) => sum + (Number(item.play_count) || 0), 0),
      airtime: uncategorised.reduce((sum, item) => sum + (Number(item.airtime_seconds) || 0), 0),
      items: uncategorised,
    });
  }
  return sections;
}

export function catalogueNote(catalogue) {
  const ids = new Set();
  sectionsFromCatalogue(catalogue).forEach((section) => {
    section.items.forEach((item) => {
      if (item.id != null) ids.add(item.id);
    });
  });
  const categories = (catalogue?.categories || []).length;
  const itemLabel = `${ids.size} item${ids.size === 1 ? '' : 's'}`;
  const categoryLabel = `${categories} categor${categories === 1 ? 'y' : 'ies'}`;
  return `${categoryLabel}, ${itemLabel}. A track filed in more than one category is listed under each, and its plays are counted in each.`;
}

export function buildCatalogueSheets(catalogue, labels = {}) {
  const sections = sectionsFromCatalogue(catalogue);
  const summary = {
    name: 'Summary',
    preamble: ['Library items by category', catalogueSubtitle(catalogue, labels), catalogueNote(catalogue)],
    headers: ['Category type', 'Category', 'Items', 'Library duration', 'Plays', 'Airtime'],
    rows: sections.map((section) => [
      section.typeLabel,
      section.name,
      section.itemCount,
      formatDurationHms(section.duration),
      section.plays,
      formatDurationHms(section.airtime),
    ]),
  };
  const period = `Plays from ${catalogue?.from || 'the start'} to ${catalogue?.to || 'now'}`;
  const allItems = {
    name: 'All items',
    preamble: [period],
    headers: ['Category type', 'Category', ...ITEM_HEADERS],
    rows: sections.flatMap((section) => section.items.map((item) => [
      section.typeLabel,
      section.name,
      ...itemColumns(item),
    ])),
  };
  const categorySheets = sections
    .filter((section) => section.items.length > 0)
    .map((section) => ({
      name: section.name,
      preamble: [
        section.typeLabel === section.name ? section.name : `${section.typeLabel} — ${section.name}`,
        `${section.itemCount} item${section.itemCount === 1 ? '' : 's'}`,
        period,
      ],
      headers: ITEM_HEADERS,
      rows: section.items.map(itemColumns),
    }));
  return [summary, allItems, ...categorySheets];
}

export function buildCatalogueDocument(catalogue, labels = {}) {
  const sections = sectionsFromCatalogue(catalogue);
  return {
    title: 'Library items by category',
    subtitle: catalogueSubtitle(catalogue, labels),
    note: catalogueNote(catalogue),
    summaryHead: ['Category type', 'Category', 'Items', 'Library duration', 'Plays', 'Airtime'],
    summaryBody: sections.map((section) => [
      section.typeLabel,
      section.name,
      String(section.itemCount),
      formatDurationHms(section.duration),
      String(section.plays),
      formatDurationHms(section.airtime),
    ]),
    sections: sections.filter((section) => section.items.length).map((section) => ({
      heading: section.typeLabel === section.name
        ? `${section.name} (${section.itemCount})`
        : `${section.typeLabel} — ${section.name} (${section.itemCount})`,
      head: ITEM_HEADERS,
      body: section.items.map((item) => itemColumns(item).map((value) => (
        typeof value === 'number' ? String(value) : (value ?? '')
      ))),
    })),
  };
}

export function catalogueFilename(catalogue, extension, dateStr) {
  const specific = catalogue?.category_id && catalogue.categories?.length === 1
    ? catalogue.categories[0].name
    : 'by-category';
  const slug = String(specific || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '') || 'by-category';
  return `library-${slug}-${dateStr}.${extension}`;
}

export async function saveCatalogueExcel(catalogue, labels, filename) {
  const document = buildCatalogueDocument(catalogue, labels);
  return saveExcelWorkbook(buildCatalogueSheets(catalogue, labels), {
    filename,
    title: document.title,
    description: `${document.subtitle} ${document.note}`,
  });
}

function hourLabel(value) {
  const hour = String(value ?? '').padStart(2, '0');
  return `${hour}:00 – ${hour}:59`;
}

function playbackGroupLabel(groupBy, row) {
  if (groupBy === 'hour') return hourLabel(row.group_label);
  if (groupBy === 'asset') return `Asset ${row.group_id || '—'} — ${row.group_label || 'Untitled'}`;
  return row.group_label || 'Unknown';
}

export function buildPlaybackReportModel({
  groupBy = 'asset',
  from,
  to,
  rows = [],
  stats,
  filters,
  formatDuration,
  formatGap,
  formatWhen,
} = {}) {
  const groupByLabel = GROUP_LABELS[groupBy] || groupBy;
  const duration = formatDuration || formatDurationHms;
  const gap = formatGap || ((value) => (value == null || value === '' ? '' : String(value)));
  const when = formatWhen || formatDateTime;
  const title = `Playback Report — By ${groupByLabel}`;
  // Downloads are often the only record of a filtered report, so the filter
  // that produced the numbers travels with the file.
  const subtitle = [
    `${from || 'Start'} to ${to || 'Now'}`,
    filters ? `Filtered: ${filters}` : '',
  ].filter(Boolean).join('   ');
  const statsLine = stats
    ? `Total plays: ${stats.total_plays || 0}  |  Unique assets: ${stats.unique_assets || 0}  |  Total airtime: ${duration(stats.total_airtime_seconds)}`
    : '';

  const timed = (row) => [
    Number(row.play_count) || 0,
    duration(row.total_duration_seconds),
    row.first_play ? when(row.first_play) : '',
    row.last_play ? when(row.last_play) : '',
  ];

  let headers;
  let body;
  if (groupBy === 'asset') {
    headers = [groupByLabel, 'Artist', 'Categories', 'Plays', 'Total Airtime', 'Avg Rotation', 'First Play', 'Last Play'];
    body = rows.map((row) => [
      playbackGroupLabel(groupBy, row),
      row.artist || '',
      (row.category_names || []).join(', '),
      Number(row.play_count) || 0,
      duration(row.total_duration_seconds),
      gap(row.avg_gap_seconds),
      row.first_play ? when(row.first_play) : '',
      row.last_play ? when(row.last_play) : '',
    ]);
  } else if (groupBy === 'hour') {
    headers = ['Hour', 'Plays', 'Total Airtime', 'First Play', 'Last Play'];
    body = rows.map((row) => [hourLabel(row.group_label), ...timed(row)]);
  } else {
    headers = [groupByLabel, 'Plays', 'Total Airtime', 'First Play', 'Last Play'];
    body = rows.map((row) => [playbackGroupLabel(groupBy, row), ...timed(row)]);
  }

  return { title, subtitle, statsLine, groupByLabel, headers, body };
}

export function playbackWorkbook(model) {
  return [{
    name: 'Playback report',
    preamble: [model.title, model.subtitle, model.statsLine].filter(Boolean),
    headers: model.headers,
    rows: model.body,
  }];
}
